// src/views/RequestsView.js

import React, { useState, useEffect, useMemo } from 'react';
import { db, functions } from '../firebase/config'; 
import { httpsCallable } from 'firebase/functions'; 
import { collection, query, orderBy, onSnapshot, doc, updateDoc, addDoc, serverTimestamp, where, getDocs } from 'firebase/firestore'; 
import { Inbox, Eye, ArrowUpDown, ArrowUp, ArrowDown, Plus, X, User, AlertTriangle } from 'lucide-react'; 
import RequestDetailModal from '../components/crm/RequestDetailModal';
import { formatPhoneNumber } from '../utils/formatters';
import { canSeeAllCustomers } from '../utils/permissions'; // s9office-6e2a9d53

// --- HELPER: NORMALIZE FOR COMPARISON ---
const normalizeStr = (str) => str ? str.toString().toLowerCase().trim() : '';
const normalizePhone = (str) => str ? str.toString().replace(/\D/g, '') : '';

// --- INTERNAL COMPONENT: MANUAL LEAD MODAL ---
const ManualLeadModal = ({ isOpen, onClose, onSubmit, isSubmitting, users, existingCustomers, existingLeads }) => {
    const [formData, setFormData] = useState({
        customerName: '', customerEmail: '', customerPhone: '',
        subject: '', description: '', source: 'Phone Inquiry', assignedTo: '' 
    });
    const [duplicateWarning, setDuplicateWarning] = useState(null);

    useEffect(() => {
        if (!isOpen) {
            setFormData({
                customerName: '', customerEmail: '', customerPhone: '',
                subject: '', description: '', source: 'Phone Inquiry', assignedTo: ''
            });
            setDuplicateWarning(null);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleChange = (e) => {
        const { name, value } = e.target;
        if (name === 'customerPhone') {
            setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }
        if (duplicateWarning) setDuplicateWarning(null);
    };

    const checkForDuplicates = () => {
        const email = normalizeStr(formData.customerEmail);
        const phone = normalizePhone(formData.customerPhone);
        if (!email && !phone) return null;

        const foundCustomer = existingCustomers.find(c => 
            (email && normalizeStr(c.email) === email) || (phone && normalizePhone(c.phone) === phone)
        );
        if (foundCustomer) return { type: 'Customer', name: foundCustomer.name, id: foundCustomer.id };

        const foundLead = existingLeads.find(l => 
            l.status !== 'Converted' && 
            ((email && normalizeStr(l.customerEmail) === email) || (phone && normalizePhone(l.customerPhone) === phone))
        );
        if (foundLead) return { type: 'Lead', name: foundLead.customerName, id: foundLead.id };

        return null;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const duplicate = checkForDuplicates();
        if (duplicate && !duplicateWarning) {
            setDuplicateWarning(duplicate);
            return;
        }
        onSubmit(formData);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 backdrop-blur-sm p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]">
                <div className="flex justify-between items-center p-4 border-b bg-gray-50 flex-shrink-0">
                    <h3 className="font-bold text-gray-800">Add New Lead</h3>
                    <button onClick={onClose} className="text-gray-500 hover:text-gray-700"><X size={20} /></button>
                </div>
                <div className="overflow-y-auto p-4 flex-grow space-y-4">
                    {duplicateWarning && (
                        <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-md flex items-start gap-3">
                            <AlertTriangle className="text-yellow-600 flex-shrink-0 mt-0.5" size={18} />
                            <div>
                                <p className="text-sm text-yellow-800 font-semibold">Possible Duplicate Found</p>
                                <p className="text-xs text-yellow-700 mt-1">{duplicateWarning.name} is already a {duplicateWarning.type}.</p>
                                <p className="text-xs text-yellow-600 mt-2 font-medium">Click "Create Lead" again to proceed anyway.</p>
                            </div>
                        </div>
                    )}
                    <form id="lead-form" onSubmit={handleSubmit} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Customer Name *</label>
                            <input type="text" name="customerName" required className="w-full p-2 border rounded" placeholder="e.g. John Doe" value={formData.customerName} onChange={handleChange} />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                                <input type="email" name="customerEmail" className="w-full p-2 border rounded" placeholder="john@example.com" value={formData.customerEmail} onChange={handleChange} />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                                <input type="tel" name="customerPhone" className="w-full p-2 border rounded" placeholder="(555) 123-4567" value={formData.customerPhone} onChange={handleChange} />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Lead Source</label>
                                <select name="source" value={formData.source} onChange={handleChange} className="w-full p-2 border rounded bg-white">
                                    <option value="Phone Inquiry">Phone Inquiry</option>
                                    <option value="Walk-in">Walk-in</option>
                                    <option value="Referral">Referral</option>
                                    <option value="Email">Email</option>
                                    <option value="Social Media">Social Media</option>
                                    <option value="Web Search">Web Search</option>
                                    <option value="Advertisement">Advertisement</option>
                                    <option value="Other">Other</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
                                <select name="assignedTo" value={formData.assignedTo} onChange={handleChange} className="w-full p-2 border rounded bg-white">
                                    <option value="">-- Unassigned --</option>
                                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                </select>
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Subject *</label>
                            <input type="text" name="subject" required className="w-full p-2 border rounded" placeholder="e.g. Kitchen Renovation" value={formData.subject} onChange={handleChange} />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                            <textarea name="description" required rows="3" className="w-full p-2 border rounded resize-none" placeholder="Details..." value={formData.description} onChange={handleChange} />
                        </div>
                    </form>
                </div>
                <div className="p-4 flex justify-end gap-3 border-t bg-gray-50 flex-shrink-0">
                    <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded">Cancel</button>
                    <button form="lead-form" type="submit" disabled={isSubmitting} className="px-4 py-2 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 rounded shadow-sm flex items-center gap-2">
                        {isSubmitting ? 'Saving...' : <><Plus size={16}/> Create Lead</>}
                    </button>
                </div>
            </div>
        </div>
    );
};

// --- MAIN COMPONENT ---
const RequestsView = ({ currentUserProfile, onCreateQuote, onViewCustomer, initialRequestId, clearInitialRequestId, onBookNow }) => {
    const [requests, setRequests] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [customers, setCustomers] = useState([]); 
    const [users, setUsers] = useState([]); 
    const [isConverting, setIsConverting] = useState(null); 
    const [selectedRequest, setSelectedRequest] = useState(null); 
    
    // Manual Lead State
    const [isLeadModalOpen, setIsLeadModalOpen] = useState(false);
    const [isSubmittingLead, setIsSubmittingLead] = useState(false);
    
    // Sorting State
    const [sortConfig, setSortConfig] = useState({ key: 'createdAt', direction: 'desc' });

    useEffect(() => {
        if (!currentUserProfile?.organizationId) return;
        const orgId = currentUserProfile.organizationId;

        // 1. Fetch Requests
        const requestsPath = `organizations/${orgId}/requests`;
        const qRequests = query(collection(db, requestsPath), orderBy('createdAt', 'desc'));
        
        const unsubscribeRequests = onSnapshot(qRequests, (snapshot) => {
            const allRequests = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                createdAt: doc.data().createdAt?.toDate(),
            }));

            // Filter logic
            // s9office-6e2a9d53 — S9b (founder decision 2026-10-04): Office Staff see ALL requests
            // (and the plain 'Owner' / 'System Admin' spellings are no longer narrowed).
            const seesAll = canSeeAllCustomers(currentUserProfile);
            const visibleRequests = allRequests.filter(req => {
                if (seesAll) return true;
                return req.assignedTo === currentUserProfile.id || !req.assignedTo;
            });

            setRequests(visibleRequests);
            setIsLoading(false); 
        });

        // 2. Fetch Customers
        const customersPath = `organizations/${orgId}/customers`;
        const qCustomers = query(collection(db, customersPath));
        const unsubscribeCustomers = onSnapshot(qCustomers, (snapshot) => {
            setCustomers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });

        // 3. Fetch Users
        const fetchUsers = async () => {
            try {
                const usersRef = collection(db, 'users');
                const qUsers = query(usersRef, where('organizationId', '==', orgId));
                const snapshot = await getDocs(qUsers);
                const usersData = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(u => u.status !== 'removed')
                    .sort((a, b) => a.name.localeCompare(b.name));
                setUsers(usersData);
            } catch (err) { console.error("Error fetching users:", err); }
        };
        fetchUsers();

        return () => { unsubscribeRequests(); unsubscribeCustomers(); };
    }, [currentUserProfile]);

    // PHASE 1.4 VERIFIER: r1-4c8f7a23
    // Notification deep-link consumer.
    // When a user clicks a 'new_lead' or 'new_request' notification, CrmApp
    // routes to /leads/<id> and passes that ID down as initialRequestId.
    // This effect opens the matching request's detail modal once the
    // requests collection has loaded. Customers list must also be loaded
    // before we open it so enrichRequestData can hydrate display fields.
    useEffect(() => {
        if (!initialRequestId || isLoading || !requests.length) return;
        const match = requests.find(r => r.id === initialRequestId);
        if (match) {
            markRequestAsViewed(match);
            setSelectedRequest(enrichRequestData(match));
            // Clear so a subsequent click on the same URL (e.g. browser back
            // then forward) re-triggers properly. CrmApp clears it via
            // clearInitialRequestId on view change.
            if (clearInitialRequestId) clearInitialRequestId();
        } else {
            // Request exists in Firestore but isn't in this user's visible set
            // (e.g. assigned to someone else and current user isn't admin).
            // Silently no-op rather than confusing the user.
            console.warn(`RequestsView: initialRequestId ${initialRequestId} not in visible requests`);
            if (clearInitialRequestId) clearInitialRequestId();
        }
        // markRequestAsViewed/enrichRequestData are stable references in this
        // component (defined inline below in render scope), so we exclude them
        // from deps. Adding them would cause this effect to re-run on every
        // render, which would re-trigger the modal open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialRequestId, isLoading, requests, customers]);

    // Helpers
    const enrichRequestData = (request) => {
        if (!request) return null;
        if (request.customerId) {
            const linkedCustomer = customers.find(c => c.id === request.customerId);
            if (linkedCustomer) {
                return {
                    ...request, 
                    customerName: linkedCustomer.name || request.customerName,
                    customerEmail: linkedCustomer.email || request.customerEmail,
                    customerPhone: linkedCustomer.phone || request.customerPhone,
                    customerAddress: linkedCustomer.address1 || '',
                };
            }
        }
        return request;
    };

    // Handlers
    const markRequestAsViewed = async (request) => {
        if (request.status === 'New' && currentUserProfile?.organizationId) {
            const requestRef = doc(db, `organizations/${currentUserProfile.organizationId}/requests`, request.id);
            try { await updateDoc(requestRef, { status: 'Viewed' }); } catch (error) {}
        }
    };

    const handleRowClick = (request) => {
        markRequestAsViewed(request);
        setSelectedRequest(enrichRequestData(request));
    };

    const handlePromoteLead = async (request) => {
        if (!currentUserProfile?.organizationId) return;
        setIsConverting(request.id); 
        const promoteLeadFn = httpsCallable(functions, 'promoteLeadToCustomer');
        try {
            const leadData = {
                customerName: request.customerName,
                customerEmail: request.customerEmail,
                customerPhone: request.customerPhone || '',
                sales_person_id: request.assignedTo || currentUserProfile.id,
                sales_person_name: request.assignedToName || currentUserProfile.name,
                status: 'New'
            };
            const result = await promoteLeadFn({
                organizationId: currentUserProfile.organizationId,
                requestId: request.id,
                leadData: leadData
            });
            if (selectedRequest?.id === request.id) setSelectedRequest(null);
            
            if (onViewCustomer && result.data?.customerId) {
                onViewCustomer({ id: result.data.customerId, ...leadData });
            } else {
                alert("Customer created successfully!");
            }
        } catch (error) {
            alert(`Failed to promote lead: ${error.message}`);
        } finally {
            setIsConverting(null); 
        }
    };

    const handleCreateQuoteWrapper = (request) => {
        markRequestAsViewed(request);
        const customer = customers.find(c => c.id === request.customerId);
        if (customer) {
            onCreateQuote(request, customer);
            setSelectedRequest(null); 
        } else {
            alert("Error: Could not find customer. Try converting the lead first.");
        }
    };

    const handleAddLead = async (formData) => {
        if (!currentUserProfile?.organizationId) return;
        setIsSubmittingLead(true);
        let assignedToName = '';
        if (formData.assignedTo) {
            const assignedUser = users.find(u => u.id === formData.assignedTo);
            if (assignedUser) assignedToName = assignedUser.name;
        }
        try {
            const requestsRef = collection(db, `organizations/${currentUserProfile.organizationId}/requests`);
            await addDoc(requestsRef, {
                ...formData,
                assignedToName,
                status: 'New',
                source: formData.source || 'Manual Entry', 
                createdAt: serverTimestamp(),
                organizationId: currentUserProfile.organizationId,
                createdBy: currentUserProfile.id
            });
            setIsLeadModalOpen(false);
        } catch (error) { alert("Failed to add lead."); } 
        finally { setIsSubmittingLead(false); }
    };

    // Sorting
    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortedRequests = useMemo(() => {
        let sortable = [...requests];
        if (sortConfig.key) {
            sortable.sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                if (aVal instanceof Date && bVal instanceof Date) return sortConfig.direction === 'asc' ? aVal - bVal : bVal - aVal;
                if (typeof aVal === 'string') aVal = aVal.toLowerCase();
                if (typeof bVal === 'string') bVal = bVal.toLowerCase();
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }
        return sortable;
    }, [requests, sortConfig]);

    const SortIcon = ({ columnKey }) => {
        if (sortConfig.key !== columnKey) return <ArrowUpDown size={14} className="ml-1 text-gray-400 opacity-50" />;
        return sortConfig.direction === 'asc' ? <ArrowUp size={14} className="ml-1 text-blue-600" /> : <ArrowDown size={14} className="ml-1 text-blue-600" />;
    };

    if (isLoading) return <p className="text-center mt-8">Loading requests...</p>;

    return (
        <div className="p-4 sm:p-6 md:p-10">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
                <h1 className="text-3xl font-bold text-gray-800">Leads</h1>
                <button onClick={() => setIsLeadModalOpen(true)} className="w-full sm:w-auto flex justify-center items-center gap-2 bg-blue-600 text-white px-4 py-2.5 rounded-lg font-bold hover:bg-blue-700 shadow-sm transition-all active:scale-95">
                    <Plus size={20} /> Add Lead
                </button>
            </div>

            <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200 min-h-[400px]">
                {/* --- DESKTOP TABLE --- */}
                <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm text-left text-gray-500">
                        <thead className="text-xs text-gray-700 uppercase bg-gray-50 border-b">
                            <tr>
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('status')}>Status <SortIcon columnKey="status" /></th>
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('customerName')}>Customer <SortIcon columnKey="customerName" /></th>
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('subject')}>Subject <SortIcon columnKey="subject" /></th>
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('source')}>Source <SortIcon columnKey="source" /></th>
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('assignedToName')}>Assigned To <SortIcon columnKey="assignedToName" /></th>
                                <th className="px-6 py-3 cursor-pointer hover:bg-gray-100" onClick={() => handleSort('createdAt')}>Date Received <SortIcon columnKey="createdAt" /></th>
                                <th className="px-6 py-3 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {sortedRequests.map(request => {
                                const isNew = request.status === 'New';
                                return (
                                    <tr key={request.id} onClick={() => handleRowClick(request)} className={`border-b cursor-pointer group hover:bg-gray-50 ${isNew ? 'bg-blue-50/60' : 'bg-white'}`}>
                                        <td className="px-6 py-4"><span className={`px-2 py-1 text-xs font-medium rounded-full ${isNew ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-800'}`}>{request.status}</span></td>
                                        <td className={`px-6 py-4 ${isNew ? 'font-bold text-gray-900' : 'text-gray-700'}`}>{request.customerName}</td>
                                        <td className={`px-6 py-4 ${isNew ? 'font-bold text-gray-900' : 'text-gray-600'}`}>{request.subject}</td>
                                        <td className="px-6 py-4 text-xs">{request.source || 'Web'}</td>
                                        <td className="px-6 py-4 text-gray-600 text-sm">
                                            <div className="flex items-center gap-1">
                                                <User size={12} className="text-gray-400"/>
                                                {request.assignedToName || <span className="text-gray-400 italic text-xs">Unassigned</span>}
                                            </div>
                                        </td>
                                        <td className={`px-6 py-4 ${isNew ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>{request.createdAt?.toLocaleDateString()}</td>
                                        <td className="px-6 py-4 text-right">
                                            <div className="flex justify-end items-center gap-2">
                                                <button className="text-gray-400 hover:text-blue-600 p-1"><Eye size={18} /></button>
                                                {!request.customerId && (
                                                    <button onClick={(e) => { e.stopPropagation(); handlePromoteLead(request); }} disabled={isConverting === request.id} className="text-green-600 hover:text-green-800 text-xs font-medium border border-green-200 px-2 py-1 rounded bg-green-50">
                                                        {isConverting === request.id ? '...' : 'Promote'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {/* --- MOBILE CARD VIEW --- */}
                <div className="md:hidden bg-gray-50 p-3 space-y-3">
                    {sortedRequests.map(request => {
                        const isNew = request.status === 'New';
                        return (
                            <div 
                                key={request.id} 
                                onClick={() => handleRowClick(request)}
                                className={`p-4 rounded-xl border shadow-sm relative active:scale-[0.99] transition-transform ${isNew ? 'bg-blue-50 border-blue-200' : 'bg-white border-gray-200'}`}
                            >
                                <div className="flex justify-between items-start mb-2">
                                    <span className={`px-2 py-1 text-xs font-bold rounded-full uppercase tracking-wider ${isNew ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600'}`}>
                                        {request.status}
                                    </span>
                                    <span className="text-xs text-gray-400">{request.createdAt?.toLocaleDateString()}</span>
                                </div>
                                
                                <h3 className="font-bold text-gray-900 text-lg">{request.customerName}</h3>
                                <p className={`text-sm mb-3 ${isNew ? 'text-blue-900 font-medium' : 'text-gray-600'}`}>{request.subject}</p>
                                
                                <div className="flex flex-wrap gap-2 mb-3">
                                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded border">{request.source || 'Web'}</span>
                                    <span className="text-xs bg-gray-100 text-gray-500 px-2 py-1 rounded border flex items-center gap-1">
                                        <User size={10}/> {request.assignedToName || 'Unassigned'}
                                    </span>
                                </div>

                                {/* Actions Footer */}
                                {!request.customerId && (
                                    <div className="pt-3 border-t border-gray-200/50 flex justify-end">
                                        <button 
                                            onClick={(e) => { e.stopPropagation(); handlePromoteLead(request); }} 
                                            disabled={isConverting === request.id} 
                                            className="w-full bg-green-100 text-green-700 py-2 rounded-lg font-bold text-sm hover:bg-green-200 transition-colors"
                                        >
                                            {isConverting === request.id ? 'Promoting...' : 'Promote to Customer'}
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>

                {requests.length === 0 && (
                    <div className="text-center py-10 text-gray-500">
                        <Inbox size={48} className="mx-auto text-gray-400" />
                        <h3 className="mt-2 text-sm font-medium text-gray-900">No new leads</h3>
                        <button onClick={() => setIsLeadModalOpen(true)} className="mt-4 text-blue-600 hover:underline text-sm font-medium">Add a lead manually</button>
                    </div>
                )}
            </div>

            {selectedRequest && (
                <RequestDetailModal 
                    request={selectedRequest} 
                    onClose={() => setSelectedRequest(null)} 
                    onConvertCustomer={handlePromoteLead} 
                    onCreateQuote={handleCreateQuoteWrapper} 
                    isConverting={isConverting === selectedRequest.id}
                    users={users} 
                    currentUserProfile={currentUserProfile}
                    onUpdate={(updatedData) => setSelectedRequest(prev => ({ ...prev, ...updatedData }))}
                    onBookNow={onBookNow}
                />
            )}
            
            <ManualLeadModal isOpen={isLeadModalOpen} onClose={() => setIsLeadModalOpen(false)} onSubmit={handleAddLead} isSubmitting={isSubmittingLead} users={users} existingCustomers={customers} existingLeads={requests} />
        </div>
    );
};

export default RequestsView;